import { useCallback, useState } from "react";
import { useAppDispatch } from "@/store";
import { reachedTerminal } from "./publicBookingFlowSlice";
import type { ServicingMscSite, SlotReservation } from "./publicBookingFlowTypes";
import { useReleaseSlotReservationMutation, useReserveSlotForZipCodeMutation } from "./publicBookingFlowApi";
import { bookingSeasonClosedTerminal } from "./bookingSeasonGate";
import { isCalThrottled } from "@/lib/calThrottled";

export const NO_SLOTS_MESSAGE = "Our appointment schedule is currently full, but funding is still available. New appointments are released periodically, often on a near-daily basis. Please check back when the next set of appointments becomes available.";

export interface ReserveForZip {
  /** Asks for a hold for the ZIP and acts on the answer. Resolves once it has. */
  reserve: (zipCode: string) => Promise<void>;
  /** True while the reservation request is in flight. */
  loading: boolean;
  /**
   * The site a hold was granted at that is not one of the applicant's preapproved sites, waiting for
   * them to accept or decline it. Null otherwise. Render AlternateSitePrompt while it is set.
   */
  pendingSite: ServicingMscSite | null;
  /** The applicant accepted pendingSite: the hold is handed to onReserved. */
  approvePendingSite: () => void;
  /** The applicant declined pendingSite: the hold is released and the flow ends at no_slots. */
  declinePendingSite: () => void;
  /**
   * The last request's 503 calThrottled (AB#514), or null: the scheduling service is busy, nothing was
   * held, and the applicant is still on the same step. Render CalThrottledNotice while it is set, with
   * `retry` as its retry. Kept through the retry itself, so the notice stays up while it is in flight.
   */
  throttled: unknown;
  /** Asks again for the ZIP the throttled request was for. */
  retry: () => void;
}

/**
 * Reserves the earliest open slot for a ZIP and handles every answer the way the ZIP step always has:
 *
 * - out of the service area: the matching out-of-area terminal
 * - no slots: the no_slots terminal
 * - availability could not be read: the error terminal
 * - booking season closed (409 bookingSeasonClosed): the booking_season_closed terminal
 * - the scheduling service is busy (503 calThrottled): no terminal; `throttled` is set, for a notice
 *   that counts down the server's wait and retries
 * - any other failure: the error terminal
 * - a hold at a preapproved site: `onReserved(zipCode, reservation)`
 * - a hold at any other site: held as `pendingSite` until the applicant accepts it (then onReserved)
 *   or declines it (then the hold is released and the flow ends at no_slots)
 *
 * Shared by the ZIP step and "Find another time" (AB#632), so that a re-reservation can never be
 * handled differently from the first one. What a hold leads to is the caller's: the ZIP step moves on
 * to step 2, "Find another time" goes back to the booking step.
 */
export function useReserveForZip(onReserved: (zipCode: string, reservation: SlotReservation) => void): ReserveForZip {
  const dispatch = useAppDispatch();
  const [reserveSlotForZipCode] = useReserveSlotForZipCodeMutation();
  const [releaseSlotReservation] = useReleaseSlotReservationMutation();
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<{ zipCode: string; reservation: SlotReservation } | null>(null);
  const [throttled, setThrottled] = useState<{ zipCode: string; error: unknown } | null>(null);

  const reserve = useCallback(async (zipCode: string) => {
    setLoading(true);
    try {
      const result = await reserveSlotForZipCode(zipCode).unwrap();
      setThrottled(null);

      switch (result.status) {
        case "outOfServiceArea": {
          const area = result.serviceArea;
          if (area?.servicedBy === "Vashon Island Support") {
            dispatch(reachedTerminal({ type: "vashon_island_support", detail: { message: area.message, phone: area.servicerPhone, extension: area.servicerPhoneExtension } }));
          } else if (area.servicedBy) {
            dispatch(reachedTerminal({ type: "out_of_area_agency", detail: { agency: area.servicedBy, phone: area.servicerPhone, servicerWebsiteLink: area.servicerWebsiteLink } }));
          } else {
            dispatch(reachedTerminal({ type: "out_of_area_211", detail: { message: area.message } }));
          }
          return;
        }
        case "noSlotsAvailable":
          dispatch(reachedTerminal({ type: "no_slots", detail: { message: NO_SLOTS_MESSAGE } }));
          return;
        // Cal could not be read, which is not a full calendar — saying "no appointments this year"
        // here would turn an outage into a year's worth of turned-away applicants. Falling out of
        // the switch unhandled, as this used to, left the applicant on a form that did nothing.
        case "unavailable":
          dispatch(reachedTerminal({ type: "error", detail: { message: "Appointment availability is temporarily unavailable. Please try again shortly." } }));
          return;
        case "reserved": {
          const { slotReservation } = result;

          if (!slotReservation.site.preapproved) {
            // Hold here until the client explicitly approves this location — it's not one of our standard preapproved sites.
            setPending({ zipCode, reservation: slotReservation });
            return;
          }

          onReserved(zipCode, slotReservation);
          return;
        }
      }
    } catch (err) {
      // Cal is throttling CMS (AB#514): a wait, not a failure, and not worth ending the flow over. The
      // applicant stays on this step and the notice asks again once the server's wait is over.
      if (isCalThrottled(err)) {
        setThrottled({ zipCode, error: err });
        return;
      }
      setThrottled(null);
      // Booking closed after this page decided it was open, or the page could not read the season
      // and showed this step anyway: the server refuses the hold, and that is a closed notice, not a
      // "something went wrong".
      const seasonClosed = bookingSeasonClosedTerminal(err);
      if (seasonClosed) {
        dispatch(reachedTerminal(seasonClosed));
        return;
      }
      dispatch(reachedTerminal({ type: "error", detail: { message: "Something went wrong. Please try again." } }));
    } finally {
      setLoading(false);
    }
  }, [reserveSlotForZipCode, dispatch, onReserved]);

  const retry = useCallback(() => {
    if (throttled) void reserve(throttled.zipCode);
  }, [throttled, reserve]);

  const approvePendingSite = useCallback(() => {
    if (!pending) return;
    onReserved(pending.zipCode, pending.reservation);
  }, [pending, onReserved]);

  const declinePendingSite = useCallback(() => {
    if (pending?.reservation.reservationUid) {
      // Best-effort: free the held slot immediately rather than waiting for it to expire on its own.
      releaseSlotReservation(pending.reservation.reservationUid).catch(() => {});
    }
    dispatch(reachedTerminal({ type: "no_slots", detail: { message: NO_SLOTS_MESSAGE } }));
  }, [pending, releaseSlotReservation, dispatch]);

  return {
    reserve,
    loading,
    pendingSite: pending?.reservation.site ?? null,
    approvePendingSite,
    declinePendingSite,
    throttled: throttled?.error ?? null,
    retry,
  };
}
